import posthog, { type CaptureResult, type Properties } from "posthog-js";
import type { RouteObject } from "react-router-dom";
import type { SurveyDisplayMode } from "@releve/shared";
import { pageNameFor, toRoutePattern } from "@/lib/analytics";

/**
 * PostHog product analytics — EU cloud, cookieless, in one of two modes per
 * page load (the first `initPostHog` wins; a second is a no-op).
 *
 * Contract (docs/OBSERVABILITY.md "Product Analytics (PostHog)"; GDPR basis in
 * docs/GDPR_COMPLIANCE.md):
 * - `persistence: 'memory'` — nothing stored in the browser, in either mode.
 *   The GDPR doc's "no analytics cookies, no consent banner" rests on this.
 *   Never call `opt_in_capturing` / `opt_out_capturing`: they write
 *   localStorage regardless of persistence.
 * - **Identified** — the app. Init waits for a signed-in user who has not
 *   opted out (`profiles.analytics_opt_out`, the Art. 21 objection) and is
 *   not on the public survey page — see components/analytics/PostHogIdentity.tsx,
 *   the only caller of the identity helpers below. It never starts on
 *   pre-login pages; after a sign-out the app hard-navigates away, so the
 *   reset instance does not capture either. The person carries the user's
 *   name and email; the organization group carries the org name. No other
 *   personal data is sent on purpose.
 * - **Anonymous** — the public survey answer page (`/s/:token`) for a
 *   visitor who is not signed in, started by PublicSurvey only. No person,
 *   no identify, no group, no per-event context, no replay, no autocapture,
 *   no `/flags` / remote config, no external scripts, no fingerprint-grade
 *   device props: one `$pageview` per load (`trackRespondentPageview`,
 *   tagged with the survey; + `$pageleave`, for Web analytics) and the
 *   explicit `RespondentEvents`.
 * - Every helper checks its mode through one guard (`identified()` /
 *   `anonymous()`), never `initialized` alone.
 * - Every URL is reduced to its route pattern before it leaves the browser
 *   (`redactEvent`), including the hrefs inside session-replay snapshots; the
 *   readable `page` property is derived from that pattern.
 * - Replay masks all text and inputs, blocks iframes/media (signed storage
 *   URLs, see sentry.ts), and masks every attribute that could carry a URL,
 *   a token or user-visible text (`maskReplayAttribute`).
 */

// rrweb's own generated attributes (`_cssText` = the inlined stylesheet,
// `rr_*` = scroll/size/media state) and structural ones — masking these
// renders replays unstyled or broken.
const REPLAY_KEEP_ATTRIBUTES = new Set([
  "_csstext", "class", "style", "id", "role", "type", "for", "name",
  "d", "viewbox", "width", "height", "fill", "stroke", "stroke-width",
  "stroke-linecap", "stroke-linejoin", "cx", "cy", "r", "x", "y", "x1", "x2",
  "y1", "y2", "rx", "ry", "points", "transform", "xmlns", "fill-rule", "clip-rule",
  "clip-path", "stroke-dasharray", "stroke-dashoffset", "preserveaspectratio", "opacity",
  "fill-opacity", "stroke-opacity", "text-anchor", "dominant-baseline",
]);

// URL-, token- or text-bearing. `data-value` is cmdk's item value, which the
// app fills with customer names/addresses (CustomerSelect, OrganizationSwitcher).
const REPLAY_MASK_ATTRIBUTES = new Set([
  "href", "src", "srcset", "action", "formaction", "poster", "data", "download",
  "title", "alt", "aria-label", "aria-description", "aria-valuetext",
  "placeholder", "value", "content", "label", "data-value",
]);

// Anything else (Radix/Tailwind `data-*` variants, aria states, …) survives
// only as a short enum-like token; free text is masked. Fails safe. Capped
// below a uuid's 36 characters so `data-row-id`-style ids are masked too.
const REPLAY_ENUM_TOKEN = /^[\w.:-]{0,32}$/;

export function maskReplayAttribute(name: string, value: string): string {
  const key = name.toLowerCase();
  const masked = "*".repeat(value.length);
  if (REPLAY_MASK_ATTRIBUTES.has(key)) return masked;
  if (REPLAY_KEEP_ATTRIBUTES.has(key) || key.startsWith("rr_")) return value;
  if (key.startsWith("aria-")) return value; // aria-label/description/valuetext masked above
  return REPLAY_ENUM_TOKEN.test(value) ? value : masked;
}

let routeObjects: RouteObject[] | null = null;

function redactUrl(value: string): string {
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return url.origin;
    return `${url.origin}${toRoutePattern(routeObjects ?? [], url.pathname)}`;
  } catch {
    return "[redacted]";
  }
}

// `$current_url`, `$referrer`, `$initial_current_url`, `$session_entry_url`,
// `$prev_pageview_pathname`, `$external_click_url`, … — any PostHog property
// that carries a URL or a path.
const URL_KEY = /(url|referrer)$/;
const PATH_KEY = /pathname$/;

function redactProperties(props: Properties | undefined): void {
  if (!props) return;
  for (const [key, value] of Object.entries(props)) {
    if (!key.startsWith("$") || typeof value !== "string" || !value) continue;
    if (URL_KEY.test(key)) props[key] = value === "$direct" ? value : redactUrl(value);
    else if (PATH_KEY.test(key)) props[key] = toRoutePattern(routeObjects ?? [], value);
  }
}

interface RrwebEvent {
  type?: number;
  data?: { href?: unknown; payload?: { href?: unknown } };
}

// rrweb Meta (type 4) and Custom (type 5 — PostHog's in-recording $pageview /
// $url_changed use `data.payload.href`, the JSON-LD event `data.href`) events
// carry the raw location.href and reach before_send uncompressed.
// Full/incremental snapshots (2/3) are gzipped before before_send, which is
// why they are masked at record time (`maskReplayAttribute`) instead.
function redactSnapshot(props: Properties): void {
  const data = props.$snapshot_data;
  if (!Array.isArray(data)) return;
  for (const event of data as RrwebEvent[]) {
    if (event?.type !== 4 && event?.type !== 5) continue;
    if (typeof event.data?.href === "string") event.data.href = redactUrl(event.data.href);
    if (event.type === 5 && typeof event.data?.payload?.href === "string") {
      event.data.payload.href = redactUrl(event.data.payload.href);
    }
  }
}

type PostHogMode = "identified" | "anonymous";

let initialized = false;
let mode: PostHogMode | null = null;
let optedOut = false;

// The one guard per mode — every helper below checks one of these.
const identified = () => initialized && !optedOut && mode === "identified";
const anonymous = () => initialized && mode === "anonymous";
// suspendPostHogRecording bookkeeping: how many never-record pages are
// mounted, and whether replay was stopped (or held back at init) on their
// behalf and should be handed back when the last one unmounts.
let recordingSuspensions = 0;
let resumeRecordingOnRelease = false;
/**
 * Who/where the user is, stamped on every event (except replay snapshots) as
 * plain properties — filterable and breakable-down on any PostHog plan (the
 * `organization` group is still set, but PostHog only shows groups with its
 * paid Group analytics add-on). Roles, modes and flags only — no personal
 * data beyond the org name. Set by PostHogIdentity; cleared on sign-out.
 */
export interface PostHogContext {
  organization_id: string | null;
  organization_name: string | null;
  org_role: string | null;
  app_mode: string | null;
  locale: string | null;
  /** Super admin (Relevé staff) — filter internal traffic out. */
  is_internal: boolean;
}

let eventContext: PostHogContext | null = null;

// Build constants: the deployed commit (vite.config exposes it) and the
// build's environment, so behaviour can be tied to a deploy.
const RELEASE = (import.meta.env.VITE_APP_RELEASE as string | undefined) || null;
const ENVIRONMENT =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) || import.meta.env.MODE;

export function redactEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || optedOut) return null;
  redactProperties(event.properties);
  redactProperties(event.$set);
  redactProperties(event.$set_once);
  if (event.event === "$snapshot") {
    redactSnapshot(event.properties);
  } else if (typeof event.properties.$current_url === "string") {
    // Stamped from the (already redacted) URL of this very event — not
    // `register`ed on navigation: history_change pageviews fire before React
    // renders, so a navigation effect would label the previous page.
    // `redactUrl` may have produced "[redacted]"; a throw here would make
    // the SDK drop the event.
    let pathname = "/[unmatched]";
    try {
      pathname = new URL(event.properties.$current_url).pathname;
    } catch {
      // keep "/[unmatched]"
    }
    event.properties.page = pageNameFor(pathname);
  }
  event.properties.$geoip_disable = true;
  // The PostHog project is shared with the marketing site ('marketing');
  // filter any view on `site` to separate the two.
  event.properties.site = "app";
  if (event.event !== "$snapshot") {
    event.properties.release = RELEASE;
    event.properties.environment = ENVIRONMENT;
    // Identified only: a signed-in user's context must never ride on a
    // respondent's events.
    if (eventContext && mode === "identified") Object.assign(event.properties, eventContext);
  }
  return event;
}

// `title` is document.title — static today, but a future dynamic title
// would carry record names; the route-derived `page` replaces it.
const BASE_DENYLIST = ["$raw_user_agent", "title"];
// Device detail that, combined, narrows an anonymous respondent down —
// dropped in anonymous mode only (the SDK applies the list as-is).
const FINGERPRINT_PROPS = [
  "$screen_height", "$screen_width", "$viewport_height", "$viewport_width",
  "$browser_version", "$os_version",
];

/**
 * Start PostHog. `identified` (the default): a signed-in user who has not
 * opted out, called by PostHogIdentity only. `anonymous`: a signed-out
 * respondent on the public survey page, called by PublicSurvey only. A no-op
 * without `VITE_POSTHOG_KEY`, in development without
 * `VITE_POSTHOG_ENABLE_DEV`, and after the first call (either mode).
 */
export function initPostHog(routes: RouteObject[], initMode: PostHogMode = "identified"): void {
  if (initialized) return;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) return;
  if (import.meta.env.MODE === "development" && !import.meta.env.VITE_POSTHOG_ENABLE_DEV) return;

  routeObjects = routes;
  initialized = true;
  mode = initMode;
  // A never-record page is already mounted (cold load of /s/:token by a
  // signed-in user): start with replay off; its release turns it on.
  // Anonymous mode never records, so it never hands replay back.
  const holdRecording = mode === "identified" && recordingSuspensions > 0;
  if (holdRecording) resumeRecordingOnRelease = true;
  posthog.init(key, {
    // Production sets VITE_POSTHOG_HOST=/relay — the same-origin proxy in
    // apps/web/vercel.json. Unset (local dev, where Vite has no /relay) goes
    // straight to EU cloud.
    api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://eu.i.posthog.com",
    ui_host: "https://eu.posthog.com",
    // Same preset as the marketing site (shared project). Every option below
    // is explicit, so the preset only fills in the rest — see the two pins
    // in session_recording.
    defaults: "2026-08-30",
    persistence: "memory",
    person_profiles: "identified_only",
    capture_pageview: "history_change",
    capture_pageleave: true,
    autocapture: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    mask_personal_data_properties: true,
    property_denylist: BASE_DENYLIST,
    // Heatmaps key their data by full URL (unreachable by before_send);
    // network timing / web vitals put fetch URLs (signed storage URLs
    // included) into replay. Explicit `false` beats remote config.
    capture_heatmaps: false,
    capture_dead_clicks: false,
    capture_performance: { network_timing: false, web_vitals: false },
    capture_exceptions: false, // Sentry owns errors
    disable_surveys: true,
    // In-house flags (docs/FEATURE_FLAGS.md). Identified mode leaves
    // `advanced_disable_flags` unset: it also blocks the remote config replay
    // needs to switch on. Known residual (docs/OBSERVABILITY.md): there
    // `/flags` still POSTs person properties (incl. the raw initial URL)
    // outside before_send — which is why PostHogIdentity defers the
    // identified init off the token page. Anonymous mode has no replay, so
    // it sets `advanced_disable_flags` below and sends no `/flags` at all.
    advanced_disable_feature_flags: true,
    // Console output carries API error payloads; canvas = map tiles. Explicit
    // `false` beats the project's remote settings.
    enable_recording_console_log: false,
    disable_session_recording: mode === "anonymous" || holdRecording,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      blockSelector: "iframe, img, video, object, embed",
      maskAttributeFn: maskReplayAttribute,
      // Explicit `false`: left unset, the project's "mask all element
      // attributes" setting would win and make the SDK drop maskAttributeFn
      // (and with it the data-value rule). `class` would be masked too.
      maskAllElementAttributes: false,
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: () => null,
      captureCanvas: { recordCanvas: false },
      // The 2026-08-30 preset turns both on. streamNetworkBody would stream
      // API request/response bodies (financial data) into replay the moment
      // network recording were enabled; JSON-LD adds nothing for the app.
      streamNetworkBody: false,
      captureJsonLd: false,
    },
    before_send: redactEvent,
    ...(mode === "anonymous"
      ? {
          // The page view is sent by hand (trackRespondentPageview) once the
          // survey has loaded, so it carries the survey's ids — an automatic
          // one would fire at init, before them. The leave is automatic.
          capture_pageview: false,
          capture_pageleave: true,
          autocapture: false,
          // Stops `/flags` and the remote-config fetch.
          advanced_disable_flags: true,
          disable_external_dependency_loading: true,
          property_denylist: [...BASE_DENYLIST, ...FINGERPRINT_PROPS],
          // The 2026-08-30 preset tags localhost visitors as internal via
          // setPersonProperties, which turns on person processing — a
          // respondent must never become a person, on any host.
          internal_or_test_user_hostname: null,
        }
      : {}),
  });
}

function capture(name: string, props: Properties, allowed: () => boolean): void {
  if (!allowed()) return;
  posthog.capture(name, props);
}

/**
 * The custom-event registry. Props are ids-free counts, enums and booleans —
 * never names, emails, amounts, descriptions or free text (the org and user
 * already ride on every event via group/identify). Add an event here first;
 * `trackEvent` only accepts registered names and their prop shapes.
 */
export interface AnalyticsEvents {
  invoice_created: { line_count: number };
  bill_created: { line_count: number };
  expense_created: { line_count: number };
  journal_created: { entry_count: number };
  bank_import_completed: {
    source: "camt" | "csv" | "enablebanking";
    inserted: number;
    skipped: number;
    failed?: number;
    truncated?: boolean;
  };
  feed_line_matched: { count: number; via: "single" | "bulk" | "pay_document" };
  feed_line_categorized: { count: number; via: "single" | "bulk" | "add_expense" };
  org_switched: Record<string, never>;
  organization_created: Record<string, never>;
  // Fired on the stream's `done` frame: a completed turn, not a send attempt.
  re_chat_message_sent: { new_thread: boolean };
  // The Ré write-tool SSE frame arrives before the tool runs: this counts
  // attempts the user confirmed in chat, not successful writes.
  re_write_tool_invoked: { tool: "save_emission_factor" | "add_custom_emission_factor" | "create_budget" | (string & {}) };
  // Ré's chart-of-accounts proposal card: shown once per message per page
  // load; committed when the Create button's commit succeeded.
  re_coa_proposal_shown: { account_count: number; warning_count: number };
  re_coa_proposal_committed: { account_count: number };
  // Surveys (staff side). Respondents use RespondentEvents below.
  survey_created: {
    source: "new" | "duplicate";
    question_count: number;
    display_mode: SurveyDisplayMode;
    has_project: boolean;
  };
  survey_status_changed: { status: "open" | "closed" };
  survey_link_shared: { method: "copy" | "qr_download" | "qr_print" };
  // A Stripe payment link of a sales channel shared from its expanded row.
  payment_link_shared: { method: "copy" | "qr_download" | "qr_print"; purpose: "donation" | "payment" };
}

/** Identified mode only. */
export function trackEvent<K extends keyof AnalyticsEvents>(name: K, props: AnalyticsEvents[K]): void {
  capture(name, props, identified);
}

export type SurveySubmitFailReason = "closed" | "empty" | "invalid" | "rateLimited" | "failed";

/**
 * Stamped on every respondent event. The ids and readable code are the one
 * sanctioned exception to "never send ids" (docs/OBSERVABILITY.md): they
 * split the funnel per survey. Never a token, question id, text or answer.
 */
export interface RespondentBase {
  survey_id: string;
  survey_code: string;
  organization_id: string;
  display_mode: SurveyDisplayMode;
  question_count: number;
}

/**
 * The respondent-funnel registry for the public survey page (anonymous mode).
 * Positions are 1-based among answerable questions and stable, because an
 * open survey's questions are frozen (RV003).
 */
// The prop-less events are `Record<never, never>`, not `Record<string, never>`
// (as in AnalyticsEvents): intersected with RespondentBase, a string index of
// `never` would refuse the base's own keys.
export interface RespondentEvents {
  survey_viewed: Record<never, never>;
  survey_question_answered: { position: number }; // 1-based among answerable questions; first edit of each (an answer cleared afterwards still counts)
  survey_step_reached: { step: number }; // one-at-a-time; 1-based; step 1 never fires
  survey_submit_blocked: { error_count: number; blank: boolean; first_invalid_position: number | null }; // client validation refused Submit, or Next in one-at-a-time
  survey_submitted: Record<never, never>; // a honeypot hit looks like one — documented
  survey_submit_failed: { reason: SurveySubmitFailReason }; // server rejections
}

/**
 * Anonymous mode only: the page's one `$pageview` (Web analytics), sent by
 * PublicSurvey once the survey request has settled. Tagged with
 * `RespondentBase` when the payload carries it; a closed survey, an unknown
 * token or an older API sends the bare page view.
 */
export function trackRespondentPageview(props: RespondentBase | null): void {
  capture("$pageview", props ? { ...props } : {}, anonymous);
}

/** Anonymous mode only. */
export function trackRespondentEvent<K extends keyof RespondentEvents>(
  name: K,
  props: RespondentEvents[K] & RespondentBase,
): void {
  capture(name, props, anonymous);
}

export interface PostHogPerson {
  email: string | null;
  name: string | null;
  isInternal: boolean;
}

/** Identify by auth user uuid with name + email; `null` on sign-out resets. */
export function identifyPostHogUser(userId: string | null, person?: PostHogPerson): void {
  // Both branches: a reset in anonymous mode would rotate the respondent's
  // distinct id mid-visit.
  if (!identified()) return;
  if (userId) {
    posthog.identify(
      userId,
      person ? { email: person.email, name: person.name, is_internal: person.isInternal } : undefined,
    );
  } else {
    eventContext = null;
    posthog.reset();
  }
}

/** Group events by organization uuid, labelled with the org name. */
export function setPostHogOrganization(organizationId: string | null, name?: string | null): void {
  if (!identified()) return;
  if (organizationId) posthog.group("organization", organizationId, name ? { name } : undefined);
  else posthog.resetGroups();
}

/** Replace the per-event context. Works before init, so the first events carry it. */
export function setPostHogContext(context: PostHogContext | null): void {
  eventContext = context;
}

/**
 * The user objected (profiles.analytics_opt_out). Drop everything from now
 * on — the flag first, so before_send refuses the final replay flush — and
 * stop the recorder. The profile page reloads after saving, so the next page
 * load never initializes PostHog at all.
 */
export function optOutOfPostHog(): void {
  optedOut = true;
  if (initialized) posthog.stopSessionRecording();
}

/**
 * Pause session replay while a page that must never be recorded is mounted —
 * the public survey answer page (`/s/:token`, docs/SURVEYS.md): a respondent
 * is never identified, and a signed-in staff member previewing the link must
 * not stream respondent-facing inputs into replay either. Returns the
 * restore function (call it on unmount; calling it twice is harmless).
 *
 * Sticky: the suspension is counted even before PostHog starts, because on a
 * cold load of the page by a signed-in user the page mounts first and
 * PostHogIdentity calls `initPostHog` only once the profile arrives. An init
 * during a suspension starts with `disable_session_recording: true`, and
 * replay is handed back to the SDK (which still honours the project's
 * sampling/remote settings) only when the last suspension is released.
 */
export function suspendPostHogRecording(): () => void {
  recordingSuspensions += 1;
  if (identified()) {
    // Always stop, even if recording has not started yet: the SDK may still be
    // waiting on remote config / sampling and would otherwise start mid-page.
    // `startSessionRecording()` with no arguments on release still honours the
    // project's sampling and remote settings.
    posthog.stopSessionRecording();
    resumeRecordingOnRelease = true;
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    recordingSuspensions -= 1;
    if (recordingSuspensions > 0 || !resumeRecordingOnRelease) return;
    resumeRecordingOnRelease = false;
    if (identified()) posthog.startSessionRecording();
  };
}
